import {useEffect,useRef,useState} from 'react';
import {UploadCancelledError,UploadCleanupError,uploadCancelledMessage,type UploadProgress,type UploadProgressHandler} from '../upload-progress';

type Operation={controller:AbortController;cleanup?:UploadCleanupError};
/** One confirmation belongs to one exact batch, including when a batch ends behind the dialog. */
export function useUploadOperation(){
 const active=useRef<Operation|null>(null),mounted=useRef(true);
 const [busy,setBusy]=useState(false),[progress,setProgress]=useState<UploadProgress|null>(null),[cancelling,setCancelling]=useState(false),[cleanupFailed,setCleanupFailed]=useState(false);
 const [confirmation,setConfirmation]=useState<Operation|null>(null),[error,setError]=useState(''),[status,setStatus]=useState('');
 useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;active.current?.controller.abort();active.current=null;};},[]);
 const current=(task:Operation)=>mounted.current&&active.current===task;
 function finish(task:Operation){if(!current(task))return;active.current=null;setBusy(false);setProgress(null);setCancelling(false);setCleanupFailed(false);setConfirmation(null);}
 function failed(task:Operation,value:unknown){
  if(!current(task))return;
  if(value instanceof UploadCleanupError){task.cleanup=value;setCleanupFailed(true);setCancelling(false);setError(value.message);return;}
  if(value instanceof UploadCancelledError)setStatus(uploadCancelledMessage(value.completedCount));
  else setError(value instanceof Error?value.message:'上传失败，请重试。');
  finish(task);
 }
 async function start(run:(onProgress:UploadProgressHandler,signal:AbortSignal)=>Promise<unknown>,successMessage=''){
  if(active.current)return;
  const task:Operation={controller:new AbortController()};active.current=task;
  setBusy(true);setProgress(null);setCancelling(false);setCleanupFailed(false);setConfirmation(null);setError('');setStatus('');
  try{await run(value=>{if(current(task)&&!task.controller.signal.aborted)setProgress(value);},task.controller.signal);if(current(task)){setStatus(successMessage);finish(task);}}
  catch(value){failed(task,value);}
 }
 function requestCancel(){if(active.current&&!active.current.controller.signal.aborted)setConfirmation(active.current);}
 function confirmCancel(){
  const task=confirmation;setConfirmation(null);
  if(!task||!current(task)||task.controller.signal.aborted)return;
  setCancelling(true);setError('');task.controller.abort();
 }
 async function retryCleanup(){
  const task=active.current;if(!task?.cleanup||cancelling)return;
  setCancelling(true);setCleanupFailed(false);setError('');
  try{const result=await task.cleanup.retry();if(current(task)){setStatus(uploadCancelledMessage(result.completedCount));finish(task);}}
  catch(value){failed(task,value);}
 }
 return {busy,progress,cancelling,cleanupFailed,confirming:!!confirmation,error,status,start,requestCancel,confirmCancel,dismissCancel:()=>setConfirmation(null),retryCleanup,clearError:()=>setError('')};
}
